import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner } from 'react-bootstrap';
import { useSearchEntrepreneurActionsLogQuery } from '../../app/api';
import { brand, cardStyle, headStyle, EntPage } from './common';

const PAGE_SIZE = 50;

const ACTION_STYLE = {
  FIRM_APPROVED:              { label: 'Firm approved',        bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  FIRM_REJECTED:              { label: 'Firm rejected',        bg: 'rgba(192,57,43,0.12)',  fg: brand.red },
  DOMAIN_VERIFIED:            { label: 'Domain verified',      bg: 'rgba(0,105,166,0.10)',  fg: brand.tealBlue },
  SEAT_REQUESTED:             { label: 'Seat requested',       bg: 'rgba(185,119,14,0.12)', fg: '#b9770e' },
  SEAT_REQUEST_WITHDRAWN:     { label: 'Seat request withdrawn', bg: 'rgba(88,88,91,0.12)', fg: brand.charcoal },
  MEMBER_ADDED_TO_CLAUDE:     { label: 'Added to Claude',      bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  MEMBER_PROVISION_REJECTED:  { label: 'Seat rejected',        bg: 'rgba(192,57,43,0.12)',  fg: brand.red },
  LIMIT_APPROVED:             { label: 'Limit approved',       bg: 'rgba(208,155,44,0.18)', fg: brand.goldenrod },
  LIMIT_REJECTED:             { label: 'Limit rejected',       bg: 'rgba(192,57,43,0.12)',  fg: brand.red },
  MEMBER_REMOVED:             { label: 'Member removed',       bg: 'rgba(88,88,91,0.12)',   fg: brand.charcoal },
  SEAT_ACTIVATION_CHARGED:       { label: 'Seat activation charged',      bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  SEAT_ACTIVATION_CHARGE_FAILED: { label: 'Seat activation charge failed', bg: 'rgba(192,57,43,0.12)', fg: brand.red },
  ANNUAL_FEE_UPDATED:            { label: 'Annual fee updated',           bg: 'rgba(0,105,166,0.10)',  fg: brand.tealBlue },
  PAYMENT_METHOD_CHANGED:        { label: 'Payment method changed',       bg: 'rgba(0,105,166,0.10)',  fg: brand.tealBlue },
  PAYMENT_METHOD_REMOVED:        { label: 'Payment method removed',       bg: 'rgba(88,88,91,0.12)',   fg: brand.charcoal },
  REFUND_ISSUED:                 { label: 'Refund issued',                bg: 'rgba(208,155,44,0.18)', fg: brand.goldenrod },
  USAGE_CHARGED:                 { label: 'Usage charged',                bg: 'rgba(26,127,78,0.12)',  fg: brand.green },
  USAGE_CHARGE_FAILED:           { label: 'Usage charge failed',          bg: 'rgba(192,57,43,0.12)',  fg: brand.red },
  STRIPE_CUSTOMERS_SYNCED:       { label: 'Stripe names synced',          bg: 'rgba(0,105,166,0.10)',  fg: brand.tealBlue },
  USAGE_CHARGE_VOIDED:           { label: 'Usage below minimum',          bg: 'rgba(88,88,91,0.12)',   fg: brand.charcoal },
};

function ActionBadge({ action }) {
  const s = ACTION_STYLE[action] || { label: action, bg: 'rgba(88,88,91,0.10)', fg: brand.charcoal };
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: '20px',
      fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}
ActionBadge.propTypes = { action: PropTypes.string };

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-US', {
  month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

// Employee-facing audit trail for every manual gate in the B2B Claude-reseller
// flow (firm approve/reject, domain verification, seat add/reject, spend-limit
// approve/reject, member removal) — "who did the manual Anthropic-console step,
// and when," for reconciliation if our records and the console ever disagree.
export default function ActionsLog() {
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setQ(qDraft.trim()), 300);
    return () => clearTimeout(id);
  }, [qDraft]);

  useEffect(() => { setPage(0); }, [q, action, actorType, dateFrom, dateTo]);

  const { data, isFetching, error } = useSearchEntrepreneurActionsLogQuery({
    q: q || undefined,
    action: action || undefined,
    actor_type: actorType || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const hasFilters = Boolean(q || action || actorType || dateFrom || dateTo);
  const clearAll = () => { setQDraft(''); setAction(''); setActorType(''); setDateFrom(''); setDateTo(''); };

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  const inputStyle = {
    border: `1px solid ${brand.softGray}`, borderRadius: '6px', padding: '4px 10px',
    fontSize: '0.8rem', color: brand.charcoal,
  };

  return (
    <EntPage title="Actions Log" subtitle="Every manual approval, verification, or removal an internal employee (or B2B firm admin) recorded for the Claude reseller flow.">
      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span>Manual actions</span>
          <span style={{ color: brand.charcoal, fontWeight: 400, fontSize: '0.8rem' }}>
            {isFetching ? 'Loading…' : total === 0 ? '0 events' : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="Search actor, member, firm, note…"
              aria-label="Search"
              style={{ ...inputStyle, width: '220px' }}
            />
            <select value={action} onChange={(e) => setAction(e.target.value)} aria-label="Action" title="Action" style={inputStyle}>
              <option value="">Any action</option>
              {Object.entries(ACTION_STYLE).map(([key, s]) => <option key={key} value={key}>{s.label}</option>)}
            </select>
            <select value={actorType} onChange={(e) => setActorType(e.target.value)} aria-label="Who" title="Who" style={inputStyle}>
              <option value="">Employee + B2B</option>
              <option value="employee">Employee only</option>
              <option value="b2b">B2B admin only</option>
            </select>
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} title="From date" style={inputStyle} />
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} title="To date" style={inputStyle} />
            {hasFilters && (
              <button type="button" onClick={clearAll} style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', color: brand.charcoal, cursor: 'pointer', fontWeight: 700 }}>
                <i className="bi bi-x-circle me-1" />Clear
              </button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.red }}>Could not load the actions log. Please try again.</div>
          ) : isFetching && rows.length === 0 ? (
            <div className="text-center p-4"><Spinner size="sm" /></div>
          ) : rows.length === 0 ? (
            <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>
              {hasFilters ? 'No actions match these filters.' : 'No manual actions recorded yet.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
                <thead style={{ background: brand.softGray }}>
                  <tr style={{ color: brand.charcoal }}>
                    <th style={{ padding: '0.6rem 0.9rem', whiteSpace: 'nowrap' }}>When</th>
                    <th style={{ padding: '0.6rem' }}>Action</th>
                    <th style={{ padding: '0.6rem' }}>Actor</th>
                    <th style={{ padding: '0.6rem' }}>Firm</th>
                    <th style={{ padding: '0.6rem' }}>Member / target</th>
                    <th style={{ padding: '0.6rem 0.9rem' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDateTime(r.created)}</td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}><ActionBadge action={r.action} /></td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        <div style={{ fontWeight: 600, color: brand.deepBlue }}>{r.actor_email || r.actor_uuid}</div>
                        <div style={{ color: brand.tealBlue, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {r.actor_type === 'b2b' ? 'B2B admin' : 'Employee'}
                        </div>
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal }}>{r.firm_name || '—'}</td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal }}>{r.target_email || r.target_name || '—'}</td>
                      <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal, wordBreak: 'break-word' }}>{r.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.75rem 1.25rem', borderTop: `1px solid ${brand.softGray}` }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
            >
              <i className="bi bi-chevron-left" /> Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => (rangeEnd < total ? p + 1 : p))}
              disabled={rangeEnd >= total}
              style={{ background: 'transparent', border: `1px solid ${brand.charcoal}`, color: brand.charcoal, borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: rangeEnd >= total ? 'not-allowed' : 'pointer', opacity: rangeEnd >= total ? 0.5 : 1 }}
            >
              Next <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </Card>
    </EntPage>
  );
}
